import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useFeatureToggle } from '@flopflip/react-redux';
import { IconButton, Tooltip } from '@mui/material';
import { MagicWandIcon } from '@sharedCR/coreDesignSystem/icons';
import { ModalSize } from '@sharedCR/coreDesignSystem/Modals/ModalCDS/ModalCDS.constants';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import { FeatureFlag } from 'constants/featureFlags';

import { selectIsAiExpressionGenerationsEnabled } from 'store/companies/selectors';
import { closeDialogAction, openDialogAction } from 'store/global/actions';

import {
  GENERATE_BUTTON_LABEL,
  GENERATE_INLINE_SCHEMA_DOMAIN,
  GENERATE_MODAL_TITLE,
} from './GenerateExpression.constants';
import { GenerateExpressionTestIds } from './GenerateExpression.testIds';
import {
  astToUserFriendlyText,
  resolveExpectedRootTypeName,
  serializeSchemaForGenerate,
} from './GenerateExpression.utils';
import GenerateExpressionModalBody from './GenerateExpressionModalBody';

const GenerateExpressionButton = ({
  schemaParams,
  value,
  onApply,
  schemaDomain = GENERATE_INLINE_SCHEMA_DOMAIN,
  isOnlyBooleanAllowed = false,
  disabled = false,
}) => {
  const dispatch = useDispatch();
  const isAiExpressionGenerationsEnabled = useSelector(selectIsAiExpressionGenerationsEnabled);
  const isNewExpressionsParsingLogicEnabled = useFeatureToggle(
    FeatureFlag.EnableNewExpressionsParsingLogic,
  );

  const onClick = useCallback(() => {
    const schemaJson = !_.isEmpty(schemaParams) ? serializeSchemaForGenerate(schemaParams) : null;
    const expectedRootTypeName = resolveExpectedRootTypeName({ isOnlyBooleanAllowed });

    const onApplyExpression = expressionJson => {
      const text = astToUserFriendlyText(expressionJson, { isNewExpressionsParsingLogicEnabled });

      if (_.isNil(text)) {
        return false;
      }

      onApply(text);
      dispatch(closeDialogAction());

      return true;
    };

    const onUndoApplyExpression = previousText => {
      onApply(previousText ?? null);
    };

    dispatch(
      openDialogAction({
        header: GENERATE_MODAL_TITLE,
        isRestyled: true,
        size: ModalSize.LARGE,
        body: () => (
          <GenerateExpressionModalBody
            currentExpressionText={value || null}
            expectedRootTypeName={expectedRootTypeName}
            isOnlyBooleanAllowed={isOnlyBooleanAllowed}
            schemaDomain={schemaDomain}
            schemaJson={schemaJson}
            schemaParams={schemaParams}
            onApplyExpression={onApplyExpression}
            onUndoApplyExpression={onUndoApplyExpression}
          />
        ),
      }),
    );
  }, [
    dispatch,
    value,
    schemaParams,
    schemaDomain,
    isOnlyBooleanAllowed,
    isNewExpressionsParsingLogicEnabled,
    onApply,
  ]);

  if (!isAiExpressionGenerationsEnabled) {
    return null;
  }

  return (
    <Tooltip placement="top" title={GENERATE_BUTTON_LABEL}>
      <span>
        <IconButton
          data-testid={GenerateExpressionTestIds.BUTTON}
          disabled={disabled}
          size="small"
          onClick={onClick}
        >
          <MagicWandIcon size={IconSize[20]} />
        </IconButton>
      </span>
    </Tooltip>
  );
};

GenerateExpressionButton.propTypes = {
  onApply: PropTypes.func.isRequired,
  schemaParams: PropTypes.object,
  schemaDomain: PropTypes.string,
  value: PropTypes.string,
  isOnlyBooleanAllowed: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default GenerateExpressionButton;
